import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, type SelectProps, styled } from "@mui/material";

import { DARK_BLUE } from "theme/colors";

type Option = {
  label: string;
  value: number | string;
};

function isOptionObject(item: number | string | Option): item is Option {
  return typeof item === "object" && "label" in item && "value" in item;
}

type MUISelectProps = SelectProps & {
  formHelperText?: string;
  options: number[] | string[] | Option[];
};

export const MUISelect: React.FC<MUISelectProps> = ({ formHelperText, label, options, ...rest }) => {
  const getSelectComponent = () => {
    return (
      <StyledSelect
        label={label}
        IconComponent={({ className }) => <FontAwesomeIcon className={className} icon={faChevronDown} />}
        {...rest}
      >
        {options.map(option => {
          const optionValue = isOptionObject(option) ? option.value : option;
          const optionLabel = isOptionObject(option) ? option.label : option;

          return (
            <MenuItem data-testid={`dropdown-option-${optionValue}`} key={optionValue} value={optionValue}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </StyledSelect>
    );
  };

  if (label || formHelperText) {
    return (
      <FormControl error={rest.error} fullWidth={rest.fullWidth} size={rest.size}>
        <InputLabel>{label}</InputLabel>
        {getSelectComponent()}
        {formHelperText && <FormHelperText>{formHelperText}</FormHelperText>}
      </FormControl>
    );
  }

  return getSelectComponent();
};

export const StyledSelect = styled(Select)<SelectProps>(({ theme }) => ({
  backgroundColor: theme.palette.common.white,

  "& .MuiSelect-select": {
    fontWeight: 500,
  },

  "& .MuiSelect-icon": {
    color: "#5c6f82",
    transform: "rotate(0deg)",
    transition: "transform 0.2s ease",
    right: 12,
  },

  "& .MuiSelect-iconOpen": {
    transform: "rotate(180deg)",
  },

  "&.Mui-disabled": {
    backgroundColor: DARK_BLUE[5],
    color: DARK_BLUE[40],

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: DARK_BLUE[20],
    },

    "& .MuiSelect-icon": {
      color: DARK_BLUE[40],
    },
  },
}));
